import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, ViewEncapsulation, ElementRef, Inject, OnInit, OnDestroy } from '@angular/core';
import { StockState } from '../../../state-management/stock/stock.reducer';
import { FinanceState } from '../../../state-management/finance/finance.reducer';
import { StockActions } from '../../../state-management/stock/stock.action';
import { FinanceActions } from '../../../state-management/finance/finance.action';
import { AppState } from '../../../interfaces/app-state.interface';
import { DialogBottomScreenComponentContent } from '../../../models/dialogs/dialog-bottom-screen';
import { UtilitiesService } from '../../../services/utilities.service';
import { TrackingActions } from '../../../state-management/tracking/tracking.action';

@AutoUnsubscribe()
@Component({
    selector: 'dialog-receipt',
    templateUrl: './dialog-receipt.component.html',
    styleUrls: ['./dialog-receipt.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class DialogReceiptComponent extends DialogBottomScreenComponentContent implements OnInit, OnDestroy {

    public stock$: Observable<StockState>;
    public pending: boolean = false;
    public finance$: Observable<FinanceState>;
    public receiptDate: Date;
    public formDatePicker: FormGroup;
    public isMobile: boolean;
    private isFixedIncome: boolean = null;
    private brokerageDates: any[] = [];
    private datesInvestment: any[] = [];
    private brokerageNotesTypesActions = {
        stock: StockActions.getBrokageNoteStock,
        future: StockActions.getBrokageNoteFuture,
        option: StockActions.getBrokageNoteOption,
    };

    constructor(
        public formBuilder: FormBuilder,
        @Inject(MAT_DIALOG_DATA)
        public data: any,
        protected elementReference: ElementRef,
        private utilitiesService: UtilitiesService,
        private dialogReference: MatDialogRef<DialogReceiptComponent>,
        private store: Store<AppState>,
    ) {

        super(elementReference);

        this.isMobile = this.utilitiesService.isMobile();
        this.stock$ = this.store.select((s) => s.stocks);
        this.pending = true;
        this.finance$ = this.store.select((s) => s.finance);
        this.isFixedIncome = this.data && this.data.module === 'fixed-income';
        this.datesInvestment = [];
        this.filterDatePicker = this.filterDatePicker.bind(this);

        this.formDatePicker = this.formBuilder.group({
            formDatePickerField: ['', Validators.required]
        });

        this.startObservers();

        if (this.isFixedIncome) {
            this.store.dispatch({ type: FinanceActions.getNoteIncomeDate });
        } else {
            this.store.dispatch({ type: StockActions.getNoteListDate });
        }

    }

    public ngOnInit() {
        super.ngOnInit();
    }

    // Não remover, necessário para o funcionamento correto do @AutoUnsubscribe
    public ngOnDestroy() {}

    // Finaliza o componente
    public dispose() {
        super.close(this.dialogReference, null);
    }

    /**
     * Filtra data diretamente pelo input do datepicker
     *
     * @param date
     */
    public filterDatePicker(date: Date) {

        const selectedDate = this.getFormatedDate(date);
        let found = false;

        if (this.datesInvestment && this.datesInvestment.length) {
            for (var i = 0; i < this.datesInvestment.length; i++) {
                found = this.datesInvestment[i] == selectedDate;
                if (found) break;
            }
        }

        return found;
    }

    /**
     * Realiza download dos comprovantes na data selecionada
     */
    public receiptDownload() {

        // Transforma a data para o formato yyyy/MM/dd
        const selectedDate = this.getFormatedDate(this.receiptDate);

        // Requisição pendente
        this.pending = true;

        // Requisita comprovantes de renda fixa
        if (this.isFixedIncome) {
            this.store.dispatch({type: FinanceActions.getBrokageNoteIncome, payload: { type: 0, date: selectedDate }});
            this.store.dispatch({type: FinanceActions.getBrokageNoteIncome, payload: { type: 1, date: selectedDate }});

        // Requisita comprovantes de bolsa
        } else {

            // Dispara requisição dos arquivos de compravante dinâmicamente de acordo com os mercados operados na data
            this.brokerageDates
                .filter((date) => this.getFormatedDate(date.referenceDate) === selectedDate && date.market)
                .map((date) => date.market.toLowerCase())
                .forEach((market) => this.store.dispatch({ type: this.brokerageNotesTypesActions[market], payload: selectedDate }));
        }

        const trackingSection = this.isFixedIncome ? 'FixedIncome' : 'Stocks';
        const trackingInfo = (this.data && this.data.trackingInfo) ? this.data.trackingInfo : {};

        this.store.dispatch({
            type: TrackingActions.trackEvents,
            payload: {
                type: `[${trackingSection}] Download Receipt`,
                events: trackingInfo,
            },
        });
    }

    /**
     * Inicia os observers do componente
     */
    private startObservers() {

        // Determina o valor da flag pending
        this.store
            .select((stock) => stock.stocks.pendingIncomeNotes)
            .subscribe((data) => this.pending = !!(data));

        this.store
            .select((stock) => stock.stocks.pending)
            .subscribe((data) => this.pending = data);

        this.store
            .select((fixedincome) => fixedincome.finance.pendingBrokageNote)
            .subscribe((data) => this.pending = !!(data));

        this.store
            .select((fixedincome) => fixedincome.finance.pending)
            .subscribe((data) => this.pending = data);

        // Carrega datas da store
        Observable.combineLatest(
            this.store.select((s) => s.stocks.incomeDates),
            this.store.select((s) => s.finance.incomeDates)
        )
        .subscribe(([stockDates, financeDates]) => {

            if (this.isFixedIncome && financeDates) {

                // Push fixed income dates
                this.pushDates(financeDates, 'operationDate');

            } else if (!this.isFixedIncome && stockDates) {

                // Push stock dates
                this.pushDates(stockDates, 'referenceDate');
                this.removeTodayFromDatelist();
            }

        });

    }

    /**
     * Insere datas recebidas no calendário
     *
     * @param dateList
     * @param objectAttribute
     */
    private pushDates(dateList: any[], objectAttribute: string) {

        // Salva todas as datas recebidas
        this.brokerageDates = dateList;

        // Filtra evitando datas duplicadas
        const dates = dateList.filter((item, nextItem, itens) => {
            return itens.map(mapedItem => mapedItem[objectAttribute].getFormatedDate())
                .indexOf(item[objectAttribute].getFormatedDate()) === nextItem;
        });

        for (let i = 0; i < dates.length; i++) {
            this.datesInvestment.push(this.getFormatedDate(dates[i][objectAttribute]));
        }

    }

    /**
     * Converte uma data para o formato esperado pelas rotas de brokeragenote (yyyy/MM/dd)
     *
     * @param date
     */
    private getFormatedDate(date: Date) {
        return new Date(date).toISOString()
            .split('T')[0]
            .split('-')
            .join('/');
    }

    /**
     * Remove o dia atual da lista de datas de movimentação, caso exista movimentação nesse dia.
     */
    private removeTodayFromDatelist = () => {
        const today: Date = new Date();
        this.datesInvestment = this.datesInvestment.filter((date) => date !== this.getFormatedDate(today));
    }
}
